import React, { useState, useEffect, useMemo } from 'react';
import {
  RiArrowLeftLine as BackIcon,
  RiDownloadLine as DownloadIcon,
  RiArrowDownSLine as ChevronDownIcon,
  RiEyeLine as PreviewIcon,
} from 'react-icons/ri';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import { ApiService } from '@/api/cases';
import clsx from 'clsx';
import { buildAllCriteriaMap, getCriterionName } from '@/utils/visaConstants';
import DocumentPreviewModal from '@/components/documents/DocumentPreviewModal';
import { DocumentWithStatus } from '@/types/document';
import { analytics } from '@/utils/analytics';
import { ANALYTICS_EVENTS } from '@/utils/analytics-events';

interface ExhibitListViewerProps {
  exhibitList: ExhibitList;
  caseId: string;
  onBack: () => void;
}

interface ExhibitList {
  id: string;
  caseId: string;
  createdAt: string;
  title: string;
  summary: string;
  visaType: string;
  totalExhibits: number;
  organizationStrategy?: string;
  recommendations?: string[];
  generatedBy: string;
  criteriaGroups: {
    tableOfContents?: string[]; // Array of exhibit titles
    exhibits: Array<{
      exhibitNumber: string;
      documentId: string;
      fileName: string;
      description: string;
      criteriaIds?: string[];
    }>;
  };
}

const ExhibitListViewer: React.FC<ExhibitListViewerProps> = ({
  exhibitList,
  caseId,
  onBack,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Document preview state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDocument, setPreviewDocument] =
    useState<DocumentWithStatus | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Build criteria map from visa-constants package (faster and more reliable than API call)
  const criteriaMap = useMemo(() => buildAllCriteriaMap(), []);

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showDownloadMenu && !target.closest('.download-menu-container')) {
        setShowDownloadMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

  const handleDownload = async (
    format: 'pdf' | 'docx',
    includeDocuments: boolean = false
  ) => {
    try {
      setDownloading(true);
      setError(null);
      setShowDownloadMenu(false);

      // Track download start
      if (includeDocuments && format === 'pdf') {
        analytics.track(
          ANALYTICS_EVENTS.EXHIBIT_LIST_DOWNLOAD_FULL_PDF_CLICKED,
          {
            case_id: caseId,
            exhibit_list_id: exhibitList.id,
            total_exhibits: exhibitList.totalExhibits,
          }
        );
      } else if (!includeDocuments) {
        if (format === 'pdf') {
          analytics.track(
            ANALYTICS_EVENTS.EXHIBIT_LIST_DOWNLOAD_INDEX_PDF_CLICKED,
            {
              case_id: caseId,
              exhibit_list_id: exhibitList.id,
              total_exhibits: exhibitList.totalExhibits,
            }
          );
        } else if (format === 'docx') {
          analytics.track(
            ANALYTICS_EVENTS.EXHIBIT_LIST_DOWNLOAD_INDEX_WORD_CLICKED,
            {
              case_id: caseId,
              exhibit_list_id: exhibitList.id,
              total_exhibits: exhibitList.totalExhibits,
            }
          );
        }
      }

      // Get file extension and MIME type
      const fileExtension = format;
      const mimeTypes = {
        pdf: 'application/pdf',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      };

      // Download file from API
      const blob = await ApiService.downloadExhibitList(
        caseId,
        exhibitList.id,
        format,
        includeDocuments
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename
      const sanitizedTitle = exhibitList.title
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase();
      const suffix = includeDocuments ? '_with_documents' : '';
      link.download = `exhibit-list-${sanitizedTitle}${suffix}.${fileExtension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess(`Exhibit list downloaded as ${format.toUpperCase()}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to download:', err);
      setError(err.message || 'Failed to download exhibit list');
    } finally {
      setDownloading(false);
    }
  };

  const handlePreviewDocument = async (
    documentId: string,
    fileName: string
  ) => {
    try {
      setLoadingPreview(true);
      setError(null);
      // Fetch full document details from the API
      const response = await ApiService.getDocument(documentId);
      console.log('Document preview response:', response);

      // Handle different response formats
      const documentData =
        response.data || (response as any).document || response;

      if (documentData && documentData.id) {
        setPreviewDocument(documentData as any);
        setPreviewModalOpen(true);
      } else {
        console.error('Invalid document data:', response);
        setError(
          'Failed to load document for preview - invalid response format'
        );
      }
    } catch (err: any) {
      console.error('Failed to load document:', err);
      setError(err.message || 'Failed to load document for preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewModalOpen(false);
    setPreviewDocument(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="flex items-center space-x-2"
          >
            <BackIcon className="h-4 w-4" />
            <span>Back to List</span>
          </Button>
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              {criteriaMap.get(exhibitList.title) ||
                exhibitList.title ||
                'Exhibit List'}
            </h2>
            <p className="text-sm text-neutral-500">
              Generated: {new Date(exhibitList.createdAt).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {/* Download Dropdown */}
          <div className="relative download-menu-container">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              disabled={downloading}
              className="flex items-center space-x-2"
            >
              <DownloadIcon className="h-4 w-4" />
              <span>{downloading ? 'Downloading...' : 'Download'}</span>
              <ChevronDownIcon className="h-4 w-4" />
            </Button>

            {showDownloadMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50 download-menu-container">
                <div className="py-1" role="menu">
                  {/* Download Index Section */}
                  <div className="px-3 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider bg-neutral-50">
                    Download Index
                  </div>
                  <button
                    onClick={() => handleDownload('pdf', false)}
                    className="block w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                    role="menuitem"
                  >
                    <div className="font-medium">PDF</div>
                    <div className="text-xs text-neutral-500">
                      Exhibit index only
                    </div>
                  </button>
                  <button
                    onClick={() => handleDownload('docx', false)}
                    className="block w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                    role="menuitem"
                  >
                    <div className="font-medium">Word</div>
                    <div className="text-xs text-neutral-500">
                      Editable document format
                    </div>
                  </button>

                  {/* Full Package Section */}
                  <div className="border-t border-neutral-200 my-1"></div>
                  <div className="px-3 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider bg-neutral-50">
                    Full Package
                  </div>
                  <button
                    onClick={() => handleDownload('pdf', true)}
                    className="block w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-100"
                    role="menuitem"
                  >
                    <div className="font-medium">PDF</div>
                    <div className="text-xs text-neutral-500">
                      Index + all original documents
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <Card padding="sm">
          <div className="text-sm text-error-600">{error}</div>
        </Card>
      )}
      {success && (
        <Card padding="sm">
          <div className="text-sm text-success-600">{success}</div>
        </Card>
      )}

      {/* Exhibit List Content */}
      <Card padding="lg">
        {/* Introduction */}
        <div className="mb-6 pb-6 border-b border-neutral-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">
              Introduction
            </h3>
            <div className="text-sm text-neutral-500">
              {exhibitList.totalExhibits} exhibits
            </div>
          </div>
          <p className="text-neutral-600">{exhibitList.summary}</p>
        </div>

        {/* Table of Contents */}
        {exhibitList.criteriaGroups?.tableOfContents &&
          exhibitList.criteriaGroups.tableOfContents.length > 0 && (
            <div className="mb-6 pb-6 border-b border-neutral-200">
              <h3 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-4">
                Table of Contents
              </h3>
              <div className="space-y-2">
                {exhibitList.criteriaGroups.tableOfContents.map((item, idx) => (
                  <div key={idx} className="text-neutral-700">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Exhibits List - Grouped by Criteria */}
        <div className="space-y-8">
          {(() => {
            // Group exhibits by criteria with CV/Bio priority (consistent with backend exports)
            const CV_BIO_KEY = '__cv_biography__';
            const criteriaGroups = new Map<
              string,
              typeof exhibitList.criteriaGroups.exhibits
            >();

            // Helper function to detect CV/Bio documents
            const isCVorBio = (exhibit: any): boolean => {
              const fileName = (exhibit.fileName || '').toLowerCase();
              const documentType = (exhibit.documentType || '').toLowerCase();
              const description = (exhibit.description || '').toLowerCase();

              return fileName.includes('cv') ||
                     fileName.includes('resume') ||
                     fileName.includes('bio') ||
                     fileName.includes('curriculum') ||
                     documentType.includes('cv') ||
                     documentType.includes('resume') ||
                     documentType.includes('biography') ||
                     description.includes('curriculum vitae') ||
                     description.includes('resume');
            };

            exhibitList.criteriaGroups?.exhibits?.forEach(exhibit => {
              // Check if this is a CV/Bio document first
              if (isCVorBio(exhibit)) {
                if (!criteriaGroups.has(CV_BIO_KEY)) {
                  criteriaGroups.set(CV_BIO_KEY, []);
                }
                criteriaGroups.get(CV_BIO_KEY)!.push(exhibit);
              } else if (exhibit.criteriaIds && exhibit.criteriaIds.length > 0) {
                // Use primary (first) criterion for grouping
                const primaryCriterion = exhibit.criteriaIds[0];
                if (!criteriaGroups.has(primaryCriterion)) {
                  criteriaGroups.set(primaryCriterion, []);
                }
                criteriaGroups.get(primaryCriterion)!.push(exhibit);
              } else {
                // Exhibits without criteria go into "Other Evidence" group
                if (!criteriaGroups.has('other')) {
                  criteriaGroups.set('other', []);
                }
                criteriaGroups.get('other')!.push(exhibit);
              }
            });

            // Build criteria order map from the criteriaMap (preserves official USCIS order)
            const criteriaOrder = Array.from(criteriaMap.keys());
            const criteriaOrderMap = new Map(criteriaOrder.map((id, idx) => [id, idx]));

            // Sort to ensure proper exhibit ordering (consistent with backend exports):
            // 1. CV/Bio first, 2. Criteria in USCIS order, 3. Other last
            const sortedGroupEntries = Array.from(criteriaGroups.entries()).sort((a, b) => {
              const [keyA] = a;
              const [keyB] = b;
              // CV/Bio should be first
              if (keyA === CV_BIO_KEY) return -1;
              if (keyB === CV_BIO_KEY) return 1;
              // 'other' should be last
              if (keyA === 'other') return 1;
              if (keyB === 'other') return -1;
              // Sort by official USCIS criteria order (not alphabetically)
              const orderA = criteriaOrderMap.get(keyA) ?? 999;
              const orderB = criteriaOrderMap.get(keyB) ?? 999;
              return orderA - orderB;
            });

            return sortedGroupEntries.map(
              ([criteriaId, exhibits], groupIndex) => (
                <div
                  key={criteriaId}
                  className="mb-8 last:mb-0"
                >
                  {/* Criteria Header - More prominent styling */}
                  <div className="bg-gradient-to-r from-primary-50 to-transparent rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-100 text-primary-700 font-bold text-sm">
                          {String.fromCharCode(65 + groupIndex)}
                        </span>
                        <h3 className="text-lg font-bold text-neutral-900">
                          {criteriaId === CV_BIO_KEY
                            ? 'CV/Biography'
                            : (criteriaId === 'other'
                              ? 'Other Evidence'
                              : criteriaMap.get(criteriaId) || criteriaId)}
                        </h3>
                      </div>
                      <span className="text-sm font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                        {exhibits.length} exhibit{exhibits.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Exhibits under this criterion - Card-based layout */}
                  <div className="space-y-4 ml-4">
                    {exhibits.map((exhibit, exhibitIndex) => (
                      <div
                        key={`${criteriaId}-${exhibitIndex}`}
                        className="bg-white rounded-lg border border-neutral-200 p-4 hover:border-primary-200 hover:shadow-sm transition-all"
                      >
                        {/* Exhibit header with number and preview button */}
                        <div className="flex items-start gap-4">
                          {/* Exhibit number badge */}
                          <div className="flex-shrink-0">
                            <div className="w-16 h-8 flex items-center justify-center bg-neutral-100 rounded-md text-sm font-bold text-neutral-700 border border-neutral-200">
                              {exhibit.exhibitNumber || `${String.fromCharCode(65 + groupIndex)}-${exhibitIndex + 1}`}
                            </div>
                          </div>

                          {/* Exhibit content */}
                          <div className="flex-1 min-w-0">
                            <div className="prose prose-sm max-w-none">
                              {exhibit.description
                                .split('\n\n')
                                .map((paragraph, pIndex) => (
                                  <p
                                    key={pIndex}
                                    className={clsx(
                                      'text-neutral-700 leading-relaxed mb-2 last:mb-0',
                                      pIndex === 0 && 'font-semibold text-neutral-900'
                                    )}
                                  >
                                    {paragraph}
                                  </p>
                                ))}
                            </div>

                            {/* File name */}
                            {exhibit.fileName && (
                              <div className="mt-2 text-xs text-neutral-500 truncate">
                                Source: {exhibit.fileName}
                              </div>
                            )}

                            {/* Display all criteria this exhibit supports (if more than current one) */}
                            {exhibit.criteriaIds &&
                              exhibit.criteriaIds.length > 1 && (
                                <div className="mt-3 pt-3 border-t border-neutral-100">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-medium text-neutral-500">
                                      Also supports:
                                    </span>
                                    {exhibit.criteriaIds
                                      .filter(id => id !== criteriaId)
                                      .map((id, idx) => (
                                        <span
                                          key={idx}
                                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-50 text-primary-700"
                                        >
                                          {criteriaMap.get(id) || id}
                                        </span>
                                      ))}
                                  </div>
                                </div>
                              )}
                          </div>

                          {/* Preview button */}
                          {exhibit.documentId && (
                            <button
                              onClick={() =>
                                handlePreviewDocument(
                                  exhibit.documentId,
                                  exhibit.fileName
                                )
                              }
                              className="flex-shrink-0 p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-transparent hover:border-primary-200"
                              title="Preview Document"
                            >
                              <PreviewIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            );
          })()}
        </div>

        {/* Organization Strategy */}
        {exhibitList.organizationStrategy && (
          <div className="mt-8 pt-6 border-t border-neutral-200">
            <h4 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-3">
              Organization Strategy
            </h4>
            <p className="text-neutral-600">
              {exhibitList.organizationStrategy}
            </p>
          </div>
        )}

        {/* Recommendations */}
        {exhibitList.recommendations &&
          exhibitList.recommendations.length > 0 && (
            <div className="mt-6 pt-6 border-t border-neutral-200">
              <h4 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-3">
                Recommendations
              </h4>
              <ul className="space-y-2">
                {exhibitList.recommendations.map((rec, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <span className="text-primary-600 mt-1">"</span>
                    <span className="text-neutral-600">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
      </Card>

      {/* Document Preview Modal */}
      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          isOpen={previewModalOpen}
          onClose={handleClosePreview}
        />
      )}
    </div>
  );
};

export default ExhibitListViewer;
